(ns ui.editors.nev-view-editor.event
  (:require [re-frame.core :as rf]
            [common.util :as cu]
            [ui.editors.nev-view-editor.model :as model]
            [ui.state.session.model :as session-model]
            [potpuri.core :as p]
            [ui.editors.card.event :as card-editor-event]
            [frontend.controller.websocket.event :as ws]
            [clojure.set :as set]

            [frontend.state.current-session :as session]
            [common.schema.entities.view :as view-schema]
            [ui.util :as uu]
            [common.schema.entities.card :as card-schema]
            [common.schema.view.single-entity :as sevs]
            [common.localization :as loc]))

(defn temp-entity?
  [entity]
  (-> entity
      :db/id
      string?))

(def inject-editor-data-interceptor
  (re-frame.core/->interceptor
   :id      ::inject-editor-data
   :before  (fn [{:keys [coeffects] :as ctx}]
              (let [db (:db coeffects)
                    view-entity (model/get-view-entity db)
                    selected-section (get-in db model/selected-section-full-path)
                    selected-top-level-section (get-in db model/selected-top-level-section-id-path)
                    view-id (:db/id view-entity)]
                (update ctx :coeffects assoc
                        :view-id view-id
                        :view-entity view-entity
                        :selected-section selected-section
                        :selected-top-level-section selected-top-level-section)))))

(def standard-intercept [rf/trim-v inject-editor-data-interceptor])

(def inject-account-id-interceptor
  (re-frame.core/->interceptor
   :id      :inject-account-id
   :before  (fn [ctx]
              (let [account-id @session/account-id]
                (update ctx :coeffects assoc :account-id account-id)))))

(defn remove-view-access-from
  [view-entity person-or-team-id]
  (let [prev-read (:view/read-access view-entity)
        prev-write (:view/write-access view-entity)
        prev-share (:view/share-access view-entity)
        new-read (disj (set prev-read) person-or-team-id)
        new-write (disj (set prev-write) person-or-team-id)
        new-share (disj (set prev-share) person-or-team-id)]
    (assoc view-entity
           :view/read-access new-read
           :view/write-access new-write
           ;; TODO: Share access may be enabled later
           #_:view/share-access #_new-share)))

;; TODO: Move these to common.schema for view and card?
(def cloned-view-entity-fields
  [:view/name
   :view/description
   :view/type
   :entity/type
   :view/cards
   :view/entity-type
   :view/configuration])

(def cloned-card-fields
  [:card/title
   :card/description
   :card/config
   :card/type
   :entity/type
   :card/entity-type])

;; TODO: Split this into smaller parts
(defn clone-view-and-cards
  [{:keys [entities account-id view-temp-id
           card-temp-ids-by-original-id]}]
  (let [view-entity (->> entities
                         (cu/filter-by :entity/type :entity.type/view)
                         (first))
        cards (->> entities
                   (cu/filter-by :entity/type :entity.type/card))
        cloned-cards
        (->> cards
             (mapv (fn [card]
                     (let [original-id (:db/id card)
                           temp-id (get card-temp-ids-by-original-id original-id)
                           cloned-card (-> card
                                           (select-keys cloned-card-fields)
                                           (assoc :db/id temp-id
                                                  :common/owner account-id
                                                  :card/access-level :card.access-level/private))]
                       cloned-card))))

        cloned-view-entity (-> view-entity
                               (select-keys cloned-view-entity-fields)
                               (assoc :db/id view-temp-id
                                      :common/owner account-id
                                      :view/access-level :view.access-level/private)
                               (update :view/name #(str % " (copy)"))

                               ;; TODO: more testing is required to determine if this replace-ids function is robust and works as expected
                               (view-schema/replace-ids card-temp-ids-by-original-id))
        cloned-entities (vec (concat [cloned-view-entity] cloned-cards))]
    cloned-entities))

(rf/reg-cofx
 ::create-temp-ids-for-view-and-cards
 (fn [cofx]
   (let [event-args (-> cofx :event first)
         {:keys [create-new-view? clone-view?
                 update-view? view-id]} event-args]
     (cond
       update-view?
       cofx

       create-new-view?
       (let [card-temp-ids-by-card-type {:card.type/tasks (uu/make-uuid)
                                         :card.type/fees (uu/make-uuid)}]
         (assoc cofx
                :view-temp-id (uu/make-uuid)
                :card-temp-ids-by-card-type card-temp-ids-by-card-type))

       clone-view?
       (let [db (:db cofx)
             view-entity (session-model/get-entity db view-id)
             view-temp-id (uu/make-uuid)
             card-temp-ids-by-original-id
             (->> view-entity
                  :view/cards
                  (mapv (fn [card-id]
                          [card-id (uu/make-uuid)]))
                  (into {}))]
         (assoc cofx
                :view-temp-id view-temp-id
                :card-temp-ids-by-original-id card-temp-ids-by-original-id))))))

(rf/reg-event-fx
 ::open-editor
 [standard-intercept
  inject-account-id-interceptor
  (rf/inject-cofx ::create-temp-ids-for-view-and-cards)]
 (fn [{:keys [db account-id view-temp-id
              card-temp-ids-by-original-id
              card-temp-ids-by-card-type]}
      [{:keys [create-new-view? clone-view? update-view?
               view-id entity-type preview-entity-id
               save-view-success-callback-event
               disable-view-success-callback-event]}]]
   (let [db-base (-> db
                     (p/dissoc-in model/root-path) ;; Clear any existing state
                     (assoc-in model/visible-full-path true)
                     (assoc-in model/entity-type-full-path entity-type)
                     (assoc-in model/primary-entity-type-path entity-type)
                     (assoc-in model/create-new-view-full-path create-new-view?)
                     (assoc-in model/clone-view-full-path clone-view?)
                     (assoc-in model/update-view-full-path update-view?)
                     (assoc-in model/location-full-path :view-editor)
                     (assoc-in model/view-editor-tab-full-path :editor)
                     (assoc-in model/sidebar-tab-full-path :sections)
                     (assoc-in model/preview-device-full-path :desktop)
                     (assoc-in model/selected-top-level-section-id-path :primary-entity)
                     (assoc-in model/selected-top-level-section-entity-type-path entity-type)
                     (assoc-in model/selected-section-full-path (sevs/summary-section-id entity-type))
                     (assoc-in model/preview-entity-id-full-path preview-entity-id)
                     (assoc-in model/save-view-success-callback-event-full-path save-view-success-callback-event)
                     (assoc-in model/preview-card-state-full-path model/preview-card-state)
                     (assoc-in model/disable-view-success-callback-event-full-path disable-view-success-callback-event))]
     (if create-new-view?
       (let [view-entity (model/create-default-view-entity
                          {:view-temp-id view-temp-id
                           :entity-type entity-type
                           :account-id account-id})

             fees-card-temp-id (:card.type/fees card-temp-ids-by-card-type)
             fees-card (card-schema/default-card
                        {:temp-id fees-card-temp-id
                         :card-type :card.type/fees
                         :entity-type entity-type
                         :account-id account-id
                         :tr loc/tr})

             tasks-card-temp-id (:card.type/tasks card-temp-ids-by-card-type)
             tasks-card (card-schema/default-card
                         {:temp-id tasks-card-temp-id
                          :card-type :card.type/tasks
                          :entity-type entity-type
                          :account-id account-id
                          :tr loc/tr})

             view-entity (-> view-entity
                             (assoc :view/cards #{fees-card-temp-id tasks-card-temp-id})
                             (assoc-in [:view/configuration
                                        :top-level-sections
                                        :primary-entity
                                        :sections
                                        (sevs/fees-section-id entity-type)
                                        :cards] [fees-card-temp-id])
                             (assoc-in [:view/configuration
                                        :top-level-sections
                                        :primary-entity
                                        :sections
                                        (sevs/tasks-section-id entity-type)
                                        :cards] [tasks-card-temp-id]))

             editor-entities {view-temp-id       view-entity
                              fees-card-temp-id  fees-card
                              tasks-card-temp-id tasks-card}]
         {:db (-> db-base
                  (assoc-in model/view-id-full-path view-temp-id)
                  (assoc-in model/entities-full-path editor-entities)
                  (model/reset-card-states-db))})
       (let [global-entities-by-id (get-in db session-model/entities-path)
             view-entity (get global-entities-by-id view-id)
             cards (->> view-entity
                        :view/cards
                        (mapv (fn [card-id]
                                (get global-entities-by-id card-id))))

             entities (if clone-view?
                        (clone-view-and-cards
                         {:entities (cons view-entity cards)
                          :account-id account-id
                          :view-temp-id view-temp-id
                          :card-temp-ids-by-original-id card-temp-ids-by-original-id})
                        (cons view-entity cards))
             view-id (->> entities
                          (cu/filter-by :entity/type :entity.type/view)
                          (first)
                          :db/id)
             editor-entities (p/index-by :db/id entities)]
         {:db (-> db-base
                  (assoc-in model/view-id-full-path view-id)
                  (assoc-in model/entities-full-path editor-entities)
                  (model/reset-card-states-db))})))))

(rf/reg-event-fx
 ::set-editor-location
 standard-intercept
 (fn [{:keys [db]} [location]]
   {:db (assoc-in db model/location-full-path location)}))

(rf/reg-event-fx
 ::set-selected-section
 standard-intercept
 (fn [{:keys [db]} [section-id]]
   {:db (assoc-in db model/selected-section-full-path section-id)}))

(rf/reg-event-fx
 ::set-selected-top-level-section
 standard-intercept
 (fn [{:keys [db view-entity]} [top-level-section-id]]
   (let [section (get sevs/sections-by-id top-level-section-id)
         ;; TODO: How to handle case where section may contain different entity types
         ;; Different section ids to separate those?
         section-entity-type (if (= top-level-section-id :primary-entity)
                               (:view/entity-type view-entity)
                               (-> section
                                   :entity-types
                                   first))
         entity-type-summary-section (sevs/summary-section-id section-entity-type)]
     {:db (-> db
              (assoc-in model/selected-top-level-section-id-path top-level-section-id)
              (assoc-in model/selected-section-full-path entity-type-summary-section)
              (assoc-in model/selected-top-level-section-entity-type-path section-entity-type))})))

(rf/reg-event-fx
 ::toggle-top-level-section-hidden
 standard-intercept
 (fn [{:keys [db view-entity]} [section-id]]
   (let [path [:view/configuration :top-level-sections section-id :hidden?]
         updated-view (update-in view-entity path not)]
     {:db (model/set-view-entity db updated-view)})))

(rf/reg-event-fx
 ::toggle-section-hidden
 standard-intercept
 (fn [{:keys [db view-entity selected-top-level-section]} [section-id]]
   (let [path [:view/configuration :top-level-sections selected-top-level-section :sections section-id :hidden?]
         updated-view (update-in view-entity path not)]
     {:db (model/set-view-entity db updated-view)})))

(rf/reg-event-fx
 ::fetch-preview-view-data
 standard-intercept
 (fn [{:keys [db view-entity]}]
   (let [preview-entity-id (get-in db model/preview-entity-id-full-path)
         cards (->> view-entity
                    :view/cards
                    (mapv (partial model/get-entity db)))]
     {:fx [[:dispatch [:ui.view.nev.event/open-view {:view-entity view-entity
                                                     :cards cards
                                                     :entity-id preview-entity-id
                                                     :state-path model/preview-state-full-path}]]]})))

(rf/reg-event-fx
 ::set-view-editor-tab
 standard-intercept
 (fn [{:keys [db]} [tab]]
   (merge
    {:db (assoc-in db model/view-editor-tab-full-path tab)}
    (when (= tab :preview)
      {:fx [[:dispatch [::fetch-preview-view-data]]]}))))

(rf/reg-event-db
 ::open-add-card-wizard
 (fn [db]
   (-> db
       (assoc-in model/location-full-path :add-card-wizard)
       (assoc-in model/add-card-wizard-step-full-path :card-type))))

(rf/reg-event-db
 ::close-editor
 (fn [db _]
   (model/close-editor db)))

(rf/reg-event-fx
 ::reorder-top-level-sections
 standard-intercept
 (fn [{:keys [db view-entity]} [start-index end-index]]
   (let [section-ord (fn [section-id]
                       (get-in view-entity
                               [:view/configuration
                                :top-level-sections
                                section-id
                                :ord]))
         ;; Only linked entity sections card be sorted; primary entity section always first
         prev-linked-entity-sorted-sections (->> view-entity
                                                 :view/configuration
                                                 :top-level-sections
                                                 (mapv first)
                                                 (sort-by section-ord)
                                                 (remove #(= :primary-entity %))
                                                 vec)
         new-sorted-sections (cu/vec-move prev-linked-entity-sorted-sections start-index end-index)

         new-linked-entity-sections-config (->> new-sorted-sections
                                                (map-indexed
                                                 (fn [ord section-id]
                                                   (let [prev-config (get-in view-entity
                                                                             [:view/configuration
                                                                              :top-level-sections
                                                                              section-id])]
                                                     [section-id (assoc prev-config :ord ord)])))
                                                (into {}))
         new-view-entity (update-in view-entity
                                    [:view/configuration
                                     :top-level-sections]
                                    merge
                                    new-linked-entity-sections-config)]
     {:db (model/set-view-entity db new-view-entity)})))

(rf/reg-event-fx
 ::reorder-cards
 standard-intercept
 (fn [{:keys [db selected-top-level-section selected-section view-entity]} [start-index end-index]]
   (let [path [:view/configuration :top-level-sections selected-top-level-section :sections selected-section :cards]
         prev-card-order (get-in view-entity path)
         new-card-order (cu/reorder-list prev-card-order start-index end-index)
         new-view-entity (assoc-in view-entity path new-card-order)]
     {:db (model/set-view-entity db new-view-entity)})))

(rf/reg-event-fx
 ::set-view-name
 standard-intercept
 (fn [{:keys [db view-entity]} [view-name]]
   {:db (model/set-view-entity db (assoc view-entity :view/name view-name))}))

(rf/reg-event-fx
 ::set-view-description
 standard-intercept
 (fn [{:keys [db view-entity]} [description]]
   {:db (model/set-view-entity db (assoc view-entity :view/description description))}))

(rf/reg-event-fx
 ::set-view-access-level
 standard-intercept
 (fn [{:keys [db view-entity]} [access-level]]
   {:db (model/set-view-entity db (assoc view-entity :view/access-level access-level))}))

(rf/reg-event-fx
 ::remove-access
 standard-intercept
 (fn [{:keys [db view-entity]} [team-or-person-id]]
   (let [new-view-entity (remove-view-access-from view-entity team-or-person-id)]
     {:db (model/set-view-entity db new-view-entity)})))

(rf/reg-event-fx
 ::add-read-access
 standard-intercept
 (fn [{:keys [db view-entity]} [team-or-account-id]]
   (let [new-view-entity (-> view-entity
                             (remove-view-access-from team-or-account-id)
                             (update :view/read-access set/union #{team-or-account-id}))]
     {:db (model/set-view-entity db new-view-entity)})))

(rf/reg-event-fx
 ::add-write-access
 standard-intercept
 (fn [{:keys [db view-entity]} [team-or-account-id]]
   (let [new-view-entity (-> view-entity
                             (remove-view-access-from team-or-account-id)
                             (update :view/write-access set/union #{team-or-account-id}))]
     {:db (model/set-view-entity db new-view-entity)})))

(rf/reg-event-fx
 ::add-share-access
 standard-intercept
 (fn [{:keys [db view-entity]} [team-or-account-id]]
   (let [new-view-entity (-> view-entity
                             (remove-view-access-from team-or-account-id)
                             (update :view/share-access set/union #{team-or-account-id}))]
     {:db (model/set-view-entity db new-view-entity)})))

(rf/reg-event-fx
 ::add-card-to-view
 standard-intercept
 (fn [{:keys [db selected-top-level-section selected-section view-entity]} [card-id]]
   (let [card (get-in db (conj session-model/entities-path card-id))
         updated-view (-> view-entity
                          (update-in [:view/cards] set/union #{card-id})
                          (update-in [:view/configuration
                                      :top-level-sections
                                      selected-top-level-section
                                      :sections
                                      selected-section
                                      :cards]
                                     (fnil conj []) card-id))
         new-db (-> db
                    (model/set-view-entity updated-view)
                    (update-in model/entities-full-path merge {card-id card})
                    (model/reset-card-states-db))]
     {:db new-db})))

(rf/reg-event-fx
 ::disable-view-success
 standard-intercept
 (fn [{:keys [db view-id]}]
   (let [callback-event (get-in db model/disable-view-success-callback-event-full-path)
         new-db (-> db
                    (model/close-editor)
                    (session-model/remove-entities [view-id]))]
     {:db new-db
      :fx [[:dispatch (conj callback-event {:view-id view-id})]]})))

(rf/reg-event-fx
 ::disable-view-error
 standard-intercept
 (fn [{:keys [db view-id]} [resp]]
   ;; TODO
   (js/console.log "disable view failure " view-id " resp " resp)
   {:db (assoc-in db model/submitting-full-path false)}))

(rf/reg-event-fx
 ::disable-view
 standard-intercept
 (fn [{:keys [db view-id]}]
   {:db (assoc-in db model/submitting-full-path true)
    ::ws/send {:id :view/disable
               :body {:view-id view-id}
               :on-success [::disable-view-success]
               :on-error [::disable-view-error]}}))

(defn one-instance-of-card-in-view?
  [view-entity card-id]
  (let [all-card-ids (->> view-entity
                          :view/configuration
                          :top-level-sections
                          (mapcat (fn [[_top-level-section-id {:keys [sections]}]]
                                    (->> sections
                                         (mapcat (fn [[_section-id {:keys [cards]}]]
                                                   cards))))))
        one-instance? (->> all-card-ids
                           (filterv (fn [id]
                                      (= id card-id)))
                           (cu/one-element?))]
    one-instance?))

(rf/reg-event-fx
 ::remove-card
 standard-intercept
 (fn [{:keys [db selected-top-level-section selected-section view-entity]} [removed-card-index]]
   (let [section-cards-path [:view/configuration
                             :top-level-sections
                             selected-top-level-section
                             :sections
                             selected-section
                             :cards]
         view-cards (:view/cards view-entity)
         section-cards (get-in view-entity section-cards-path)
         removed-card-id (nth section-cards removed-card-index)
         only-instance-in-view? (one-instance-of-card-in-view? view-entity removed-card-id)
         new-section-cards (cu/vec-remove removed-card-index section-cards)
         new-view-cards (if only-instance-in-view?
                          (disj view-cards removed-card-id)
                          view-cards)
         new-view-entity (-> view-entity
                             (assoc :view/cards new-view-cards)
                             (assoc-in section-cards-path new-section-cards))
         new-db (-> db
                    (model/set-view-entity new-view-entity))]
     {:db new-db})))

(rf/reg-event-fx
 ::exit-add-card-wizard
 standard-intercept
 (fn [{:keys [db]}]
   {:db (-> db
            (assoc-in model/location-full-path :view-editor)
            (p/dissoc-in model/add-card-wizard-step-full-path)
            (p/dissoc-in model/add-card-wizard-card-type-full-path))
    :fx [[:dispatch [::card-editor-event/clear-editor-state]]]}))

(rf/reg-event-fx
 ::add-card-wizard-previous-step
 standard-intercept
 (fn [{:keys [db]}]
   (let [current-step (get-in db model/add-card-wizard-step-full-path)
         prev-step (case current-step
                     :configure-card :card-template
                     :card-template :card-type)]
     (merge
      {:db (assoc-in db model/add-card-wizard-step-full-path prev-step)}
      (when (= current-step :configure-card)
        {:fx [[:dispatch [::card-editor-event/clear-editor-state]]]})))))

(rf/reg-event-db
 ::select-card-type
 standard-intercept
 (fn [db [card-type]]
   (-> db
       (assoc-in (conj model/root-path :add-card-wizard-step) :card-template)
       (assoc-in (conj model/root-path :add-card-wizard-card-type) card-type))))

(rf/reg-event-db
 ::set-add-card-wizard-step
 standard-intercept
 (fn [db [step]]
   (assoc-in db model/add-card-wizard-step-full-path step)))

(rf/reg-event-db
 ::set-preview-device
 standard-intercept
 (fn [db [preview-device]]
   (assoc-in db model/preview-device-full-path preview-device)))

(rf/reg-event-fx
 ::close-card-editor
 standard-intercept
 (fn [{:keys [db selected-top-level-section selected-section view-id view-entity]}
      [{:keys [new-card?]} {:keys [apply-changes? card]}]]
   (let [new-db-base (-> db
                         (assoc-in model/location-full-path :view-editor)
                         (assoc-in model/visible-full-path true)
                         (p/dissoc-in model/add-card-wizard-step-full-path)
                         (p/dissoc-in model/add-card-wizard-card-type-full-path))]
     {:db (cond
            (and apply-changes? new-card?)
            (let [card-id (:db/id card)
                  updated-view (-> view-entity
                                   (update :view/cards set/union #{card-id})
                                   (update-in [:view/configuration
                                               :top-level-sections
                                               selected-top-level-section
                                               :sections
                                               selected-section
                                               :cards] (fnil conj []) card-id))]
              (-> new-db-base
                  (model/merge-entities {card-id card
                                         view-id updated-view})))

            apply-changes?
            (model/merge-entity new-db-base card)

            :else
            new-db-base)})))

(rf/reg-event-fx
 ::open-card-editor
 standard-intercept
 (fn [{:keys [db]} [{:keys [card card-type new-card?]}]]
   (let [entity-type (get-in db model/selected-top-level-section-entity-type-path)
         card-type (or card-type (:card/type card))
         preview-entity-id (get-in db model/preview-entity-id-full-path)]
     {:fx [[:dispatch [::card-editor-event/initialize
                       {:card card
                        :card-type card-type
                        :new-card? new-card?
                        :entity-type entity-type
                        :update-card? true
                        :preview-entity-id nil
                        :close-editor-callback-event [::close-card-editor
                                                      {:new-card? false}]}]]
           [:dispatch [::set-editor-location :card-editor]]]})))

(rf/reg-event-fx
 ::open-card-configuration-step
 standard-intercept
 (fn [{:keys [db]} _]
   (let [card-type (get-in db (conj model/root-path :add-card-wizard-card-type))
         entity-type (get-in db model/selected-top-level-section-entity-type-path)
         preview-entity-id (get-in db model/preview-entity-id-full-path)]
     {:fx [[:dispatch [::card-editor-event/initialize
                       {:card nil
                        :card-type card-type
                        :entity-type entity-type
                        :update-card? false
                        :create-new-card? true
                        :preview-entity-id nil
                        :close-editor-callback-event [::close-card-editor
                                                      {:new-card? true}]}]]
           [:dispatch [::set-add-card-wizard-step :configure-card]]]})))

(rf/reg-event-fx
 ::set-preview-entity-id
 standard-intercept
 (fn [{:keys [db]} [preview-entity-id]]
   {:db (-> db
            (assoc-in model/preview-entity-id-full-path preview-entity-id)
            (model/reset-card-states-db))
    :fx [[:dispatch [::fetch-preview-view-data]]]}))

(rf/reg-event-fx
 ::upsert-view-and-cards-success
 standard-intercept
 (fn [{:keys [db]} [resp]]
   (let [entities (-> resp :body :entities)
         view-entity (->> entities
                          (cu/find-one :entity/type :entity.type/view))
         entities-by-id (p/index-by :db/id entities)
         callback-event (get-in db model/save-view-success-callback-event-full-path)]
     {:db (-> db
              (session-model/merge-entities entities-by-id)
              (model/close-editor))
      :fx [[:dispatch (conj callback-event {:view-entity view-entity})]]})))

(rf/reg-event-fx
 ::upsert-view-and-cards-error
 standard-intercept
 (fn [{:keys [db]} [resp]]
   (js/console.log "upsert view and cards error " resp)
   {:db (p/dissoc-in db model/submitting-full-path)}))

(defn resolve-view-or-card-updates
  [original-entities-by-id entity]
  (let [id (:db/id entity)
        original-entity (get original-entities-by-id id)
        entity-type (:entity/type entity)]
    (cond->> entity
      (= entity-type :entity.type/view)
      view-schema/select-update-fields

      (= entity-type :entity.type/card)
      card-schema/select-update-fields

      true (filterv (fn [[field value]]
                      (->> field
                           (get original-entity)
                           (not= value))))
      true (into {}))))

(rf/reg-event-fx
 ::upsert-view-and-cards
 standard-intercept
 (fn [{:keys [db]}]
   (let [view-entity (model/get-view-entity db)
         cards (model/get-cards-db db)
         original-entities-by-id (session-model/get-entities db)
         all-entities (cons view-entity cards)

         temp-entities
         (->> all-entities
              (filter temp-entity?)
              (mapv (fn [entity]
                      (let [id (:db/id entity)
                            entity-type (:entity/type entity)]
                        (cond-> entity
                          (= entity-type :entity.type/view) view-schema/select-create-fields
                          (= entity-type :entity.type/card) card-schema/select-create-fields
                          true (assoc :db/id id :entity/type entity-type))))))

         updated-real-entities
         (->> all-entities
              (remove temp-entity?)
              (mapv (fn [entity]
                      {:entity-id (:db/id entity)
                       :entity-type (:entity/type entity)
                       :updates (resolve-view-or-card-updates original-entities-by-id entity)}))
              ;; TODO: view must be always sent for now for updating the updated on attributes
              ;; related to the permission checking for cards
              (filterv (fn [{:keys [updates entity-type]}]
                         (or (= entity-type :entity.type/view)
                             (seq updates))))
              (mapv (fn [{:keys [entity-id entity-type updates]}]
                      (-> updates
                          (assoc :db/id entity-id
                                 :entity/type entity-type)))))

         entities (vec (concat temp-entities updated-real-entities))]
     {:db (assoc-in db model/submitting-full-path true)
      ::ws/send {:id :view/upsert-view-and-cards
                 :body {:entities entities}
                 :on-success [::upsert-view-and-cards-success]
                 :on-error [::upsert-view-and-cards-error]}})))
